import type { Asset } from '@repo/db/queries/assets'
import { z } from 'zod'

/**
 * The seam between "bytes are in the store" and "the database knows about
 * them". Shared by every image use-case — avatars, equipment covers, studio
 * headers — because the shape of an upload does not depend on what it is a
 * picture OF.
 *
 * `@repo/core` never touches the store itself: uploading needs a token and a
 * `File`, neither of which belongs in a use-case. The app uploads, then hands
 * the result here to be indexed, and deletes the blobs this layer reports as
 * superseded.
 */

/**
 * What an app may say about an upload it just performed.
 *
 * A URL is accepted only because the app produced it moments earlier. It must
 * never come from a form field: an arbitrary URL rendered as a site image is
 * both an SSRF-shaped hole and a tracking pixel with our markup around it. Every
 * use-case taking this input is fed by a server action that did the upload
 * itself — that is the invariant, and it is why no `image`/`url` field appears
 * on any of the editorial update schemas.
 */
export const UploadedImageInput = z.object({
  url: z.string().url().max(2048),
  pathname: z.string().trim().min(1).max(512),
  contentType: z.string().trim().min(1).max(128),
  sizeBytes: z.number().int().positive(),
  originalFileName: z.string().trim().max(255).nullable().optional(),
  /** Alt text for the marketing surface; a11y is not optional on public imagery. */
  altText: z.string().trim().max(300).nullable().optional(),
})
export type UploadedImageInput = z.infer<typeof UploadedImageInput>

/**
 * A blob nothing points at any more. Narrowed to the two fields the store needs
 * to delete it — the caller has no business with the rest of the asset row, and
 * a full `Asset` here would tempt a caller into re-linking a soft-deleted one.
 */
export interface SupersededBlob {
  readonly url: string
  readonly pathname: string
}

export function toSupersededBlob(row: Asset): SupersededBlob {
  return { url: row.url, pathname: row.pathname }
}

/** A picture as any surface renders it: the URL plus what to say when it fails. */
export interface ImageRef {
  readonly url: string
  readonly altText: string | null
}

export function toImageRef(row: Asset): ImageRef {
  return { url: row.url, altText: row.altText }
}
